/**
 * Beschikbare lettertypes voor het thema, geserveerd via Bunny.net
 * (privacyvriendelijke drop-in voor Google Fonts, zelfde catalogus).
 * Bewust een gecureerde lijst — kwaliteit boven kwantiteit — die zonder
 * externe API-call werkt. De slug voor de CSS-URL van Bunny is de naam
 * in kleine letters met streepjes (bunnyFontSlug).
 */

export type FontCategory = 'sans-serif' | 'serif' | 'monospace'

export interface BunnyFont {
  name: string
  category: FontCategory
}

export const BUNNY_FONTS: BunnyFont[] = [
  // Sans-serif
  { name: 'Archivo', category: 'sans-serif' },
  { name: 'Barlow', category: 'sans-serif' },
  { name: 'Cabin', category: 'sans-serif' },
  { name: 'DM Sans', category: 'sans-serif' },
  // Onze eigen koplettertype: stond niet in de lijst, terwijl klanten er
  // net zo goed recht op hebben als op de rest van de catalogus.
  { name: 'Encode Sans', category: 'sans-serif' },
  { name: 'Figtree', category: 'sans-serif' },
  { name: 'Fira Sans', category: 'sans-serif' },
  { name: 'Heebo', category: 'sans-serif' },
  { name: 'IBM Plex Sans', category: 'sans-serif' },
  { name: 'Inter', category: 'sans-serif' },
  { name: 'Josefin Sans', category: 'sans-serif' },
  { name: 'Karla', category: 'sans-serif' },
  { name: 'Lato', category: 'sans-serif' },
  { name: 'Lexend', category: 'sans-serif' },
  { name: 'Libre Franklin', category: 'sans-serif' },
  { name: 'Manrope', category: 'sans-serif' },
  { name: 'Montserrat', category: 'sans-serif' },
  { name: 'Mulish', category: 'sans-serif' },
  { name: 'Nunito', category: 'sans-serif' },
  { name: 'Nunito Sans', category: 'sans-serif' },
  { name: 'Open Sans', category: 'sans-serif' },
  { name: 'Outfit', category: 'sans-serif' },
  { name: 'Plus Jakarta Sans', category: 'sans-serif' },
  { name: 'Poppins', category: 'sans-serif' },
  { name: 'PT Sans', category: 'sans-serif' },
  { name: 'Quicksand', category: 'sans-serif' },
  { name: 'Raleway', category: 'sans-serif' },
  { name: 'Roboto', category: 'sans-serif' },
  { name: 'Rubik', category: 'sans-serif' },
  { name: 'Sora', category: 'sans-serif' },
  { name: 'Source Sans 3', category: 'sans-serif' },
  { name: 'Space Grotesk', category: 'sans-serif' },
  { name: 'Urbanist', category: 'sans-serif' },
  { name: 'Work Sans', category: 'sans-serif' },
  // Serif
  { name: 'Bitter', category: 'serif' },
  { name: 'Cormorant Garamond', category: 'serif' },
  { name: 'Crimson Text', category: 'serif' },
  { name: 'EB Garamond', category: 'serif' },
  { name: 'Libre Baskerville', category: 'serif' },
  { name: 'Lora', category: 'serif' },
  { name: 'Merriweather', category: 'serif' },
  { name: 'Playfair Display', category: 'serif' },
  { name: 'PT Serif', category: 'serif' },
  { name: 'Roboto Slab', category: 'serif' },
  { name: 'Source Serif 4', category: 'serif' },
  { name: 'Zilla Slab', category: 'serif' },
  // Monospace
  { name: 'Fira Code', category: 'monospace' },
  { name: 'IBM Plex Mono', category: 'monospace' },
  { name: 'JetBrains Mono', category: 'monospace' },
  { name: 'Source Code Pro', category: 'monospace' },
  { name: 'Space Mono', category: 'monospace' },
]

/** Slug zoals Bunny.net die in de css-URL verwacht ("Source Sans 3" → "source-sans-3"). */
export function bunnyFontSlug(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-')
}

/** Volledige font-family-waarde met passende generieke fallback. */
export function fontFamilyCss(name: string): string {
  const category = BUNNY_FONTS.find((f) => f.name === name)?.category ?? 'sans-serif'
  const fallback = category === 'sans-serif'
    ? "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    : category
  return `'${name}', ${fallback}`
}

/**
 * Stylesheet-URL die alle opgegeven fonts in de gangbare gewichten laadt.
 * Gewichten die een font niet heeft, slaat Bunny stilzwijgend over.
 */
export function bunnyFontsUrl(names: string[]): string {
  const family = names.map((name) => `${bunnyFontSlug(name)}:400,500,600,700`).join('|')
  return `https://fonts.bunny.net/css?family=${family}&display=swap`
}
